import React, { useState } from 'react'
import { css, keyframes } from 'emotion'
import { Spacer, Text, View } from 'oio-react'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CheckmarkIcon, LikeHeartIcon, MoreIcon } from 'assets/icons'
import FileListItemRowSmall from 'src/apps/Files/components/FileListItemRowSmall'
import UserBadgeSash from 'src/apps/People/components/UserBadgeSash'
import { downloadFileAsset } from 'src/components/utils'
import RichText from 'src/components/utils/RichText'
import { useUpdateMessageLiked } from 'src/graphql/hooks'
import MessagesContext from '~/components/Context'
import MessageInput from '~/components/Input'
import ReplyListItem from '~/components/ReplyListItem'

const animateOut = keyframes`
   0% { opacity: 1; }
   80% { opacity: 1; }
   100% { opacity: 0; }
`

// =======================================================
// Component
// =======================================================

const MessageListItem = ({
   id,
   allowMarkClosingMessage,
   allowMessageCreate,
   anon,
   authorAvatarUrl,
   authorBadges,
   authorId,
   authorName,
   body,
   borderStyle,
   edited,
   flaggedByMe,
   currentUserCanModerate,
   dateAdded,
   isClosingMessage,
   isLikedByMe,
   isMine,
   numLikes,
   onCloseMessageButtonClick,
   replies,
   showNewMessageAnchor,
   textColor,
   textLineHeight,
   textSize
}) => {
   const { updateMessageLiked } = useUpdateMessageLiked()
   const [replyInputIsVisible, setReplyInputIsVisible] = useState(false)
   const avatarUrl = authorAvatarUrl || '/assets/images/avatarPlaceholder.svg'

   // TODO: Improve this later
   // We check that the second block (element[1]) is a files block
   // (this is to make sure legacy GNS3 discussions dont blow up)
   // See PR #563, #551
   const hasFileAttachments = body.elements?.[1]?.__typename === 'FileListElement' &&
      body.elements?.[1]?.fileInstances.length > 0

   const handleMessageLikeToggled = async (liked) => {
      await updateMessageLiked({ id, liked }, {
         optimisticResponse: {
            updateMessageLiked: {
               __typename: 'HumanMessage',
               id,
               isLikedByMe: liked,
               numLikes: liked
                  ? numLikes + 1
                  : numLikes - 1
            }
         }
      })
   }

   return (
      <View
         float="left"
         width="100%"
         border={isClosingMessage && '2px solid rgb(69, 194, 145)'}
         borderRadius={isClosingMessage && '2px'}
         borderTop={!isClosingMessage && borderStyle}>
         {isClosingMessage && (
            <View
               display="flex"
               alignItems="center"
               backgroundColor="rgb(69, 194, 145)"
               padding="10px 15px 15px 15px">
               <CheckmarkIcon width="12px" height="12px" color="#fff" />
               <Spacer size="2" orientation="vertical" />
               <Text size="1.5" weight="medium" color="#fff">
                  This answers the question
               </Text>
            </View>
         )}
         <View
            display="flex"
            padding={isClosingMessage
               ? '15px'
               : '15px 0px[a-b] 15px 10px[c-f]'
            }>
            <View
               flex="0 0 auto"
               float="left"
               width="30px"
               height="30px"
               borderRadius="3px"
               backgroundColor="#ddd"
               backgroundImage={`url(${avatarUrl})`}
               backgroundSize={authorAvatarUrl ? 'cover' : '21px 21px'}
               backgroundPosition="bottom center"
               backgroundRepeat="no-repeat"
               marginRight="20px"
               marginTop="5px"
            />
            <View flex="1 1 auto" maxWidth="calc(100% - 50px)">
               <View
                  display="flex"
                  justifyContent="space-between">
                  <View
                     display="block[a-b] flex[c-f]"
                     alignItems="center">
                     <View display="inline-flex" alignItems="center" paddingRight="5px">
                        {!anon && (
                           <Link to={`/profile/${authorId}`}>
                              <Text size="2" color="#222" weight="medium">
                                 {anon ? 'Anonymous' : authorName}
                              </Text>
                           </Link>
                        )}
                        {anon && (
                           <Text size="2" color="#222" weight="medium">
                              Anonymous
                           </Text>
                        )}
                        {!anon && (
                           <UserBadgeSash badges={authorBadges} style={{ margin: '0 0 0 5px' }} />
                        )}
                     </View>
                     <View opacity="0.5">
                        <Text size="1.5" color="#222">
                           {dateAdded}
                           {edited && (
                              <View display="inline-block" marginLeft="5px" opacity="0.8">
                                 <Text size="1" color="#222">[edited]</Text>
                              </View>
                           )}
                        </Text>
                     </View>
                     {flaggedByMe && (
                        <View display="inline-block" marginLeft="5px">
                           <Text size="1" color="orange">[Reported]</Text>
                        </View>
                     )}
                  </View>
                  <MessagesContext.ThreadEntryOptionsButton
                     messageId={id}
                     threadEntryBody={body}
                     threadEntryType="message"
                     userCanEdit={isMine}
                     userCanRemove={isMine || currentUserCanModerate}
                     userCanReport={!isMine}>
                     <MoreIcon
                        width="24px"
                        height="24px"
                        color={textColor}
                     />
                  </MessagesContext.ThreadEntryOptionsButton>
               </View>
               <View display="block" width="100%" maxWidth="700px">
                  <Text
                     size={textSize}
                     color={textColor}
                     lineHeight={textLineHeight}>
                     <RichText html={body?.elements?.[0]?.body} />
                  </Text>
                  {hasFileAttachments && (
                     <View
                        float="left"
                        width="100%"
                        margin="15px 0px"
                        borderTop="1px solid #eee"
                        borderLeft="1px solid #eee"
                        borderRight="1px solid #eee">
                        {body.elements[1].fileInstances.map(instance => (
                           <FileListItemRowSmall
                              key={instance.file.id}
                              id={instance.file.id}
                              infected={instance.file.infected}
                              mimetypeMismatch={instance.file.mimetypeMismatch}
                              mimetypeVerified={instance.file.mimetypeVerified}
                              name={instance.file.name}
                              onDownloadButtonClick={() => {
                                 downloadFileAsset(instance.file.id)
                              }}
                              paddingHorizontal="5px"
                              showDownloadButton
                              status={instance.file.status}
                              thumbnailUrl={instance.file.thumbnailUrlW48}
                           />
                        ))}
                     </View>
                  )}
                  {numLikes > 0 && (
                     <View float="left" width="100%" marginTop="6px">
                        <Text
                           size="1"
                           lineHeight="150%"
                           color={textColor}>
                           {`${pluralize('people', numLikes, true)} like${numLikes === 1 ? 's' : ''} this`}
                        </Text>
                     </View>
                  )}
               </View>
               <View float="left" width="100%" display="block">
                  <Spacer size="1" />
                  <View display="flex" alignItems="center">
                     <View
                        onClick={() => handleMessageLikeToggled(!isLikedByMe)}
                        display="inline-flex"
                        alignItems="center"
                        opacity={isLikedByMe ? '1' : '0.5'}
                        style={{ cursor: 'pointer' }}>
                        {isLikedByMe && (
                           <View display="inline-block" paddingRight="6px">
                              <LikeHeartIcon width="12px" height="12px" color="#ff5b5b" />
                           </View>
                        )}
                        <Text
                           size="1"
                           weight="bold"
                           color={isLikedByMe ? '#ff5b5b' : textColor}>
                           {isLikedByMe ? 'Liked' : 'Like'}
                        </Text>
                     </View>
                     {allowMessageCreate && !replyInputIsVisible && (
                        <View
                           onClick={() => setReplyInputIsVisible(true)}
                           display="inline-flex"
                           alignItems="center"
                           opacity="0.5"
                           style={{ cursor: 'pointer' }}>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="1" weight="bold" color={textColor}>
                              &#183;
                           </Text>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="1" weight="bold" color={textColor}>
                              Reply
                           </Text>
                        </View>
                     )}
                     {(isMine || currentUserCanModerate) && allowMarkClosingMessage && (
                        <View
                           onClick={onCloseMessageButtonClick}
                           display="inline-flex"
                           alignItems="center"
                           opacity="0.5"
                           style={{ cursor: 'pointer' }}>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="1" weight="bold" color={textColor}>
                              &#183;
                           </Text>
                           <Spacer size="1.5" orientation="vertical" />
                           <Text size="1" weight="bold" color={textColor}>
                              Mark as Answer
                           </Text>
                        </View>
                     )}
                  </View>
                  {replies.map(reply => (
                     <ReplyListItem
                        key={reply.id}
                        id={reply.id}
                        anon={reply.anon}
                        authorAvatarUrl={reply?.author?.avatarMedia?.file.thumbnailUrlW48}
                        authorBadges={reply?.author?.badges}
                        authorId={reply?.author?.id}
                        authorName={reply?.author?.fullName}
                        body={reply.body?.elements?.[0]?.body}
                        borderStyle={borderStyle}
                        currentUserCanModerate={currentUserCanModerate}
                        dateAdded={reply.dateAdded}
                        edited={reply.edited}
                        flaggedByMe={reply.flaggedByMe}
                        isLikedByMe={reply.isLikedByMe}
                        isMine={reply.isMine}
                        messageId={id}
                        numLikes={reply.numLikes}
                        textColor={textColor}
                     />
                  ))}
                  {replyInputIsVisible && (
                     <View float="left" width="100%" paddingTop="10px">
                        <MessageInput
                           inputExpandedOnStart
                           inputToolbarConfig="textBasic"
                           messageId={id}
                           onCancel={() => setReplyInputIsVisible(false)}
                           onSubmit={() => setReplyInputIsVisible(false)}
                        />
                     </View>
                  )}
               </View>
            </View>
         </View>
         {showNewMessageAnchor && (
            <View
               className={css`
                  opacity: 0;
                  animation: ${animateOut} 10s ease-out;
               `}
               position="relative"
               top="1px"
               borderTop="1px solid #3893f2">
               <View
                  display="flex"
                  alignItems="center"
                  height="16px"
                  position="absolute"
                  top="-8px"
                  padding="0px 10px"
                  borderRadius="2px"
                  backgroundColor="#3893f2">
                  <Text size="0.8" weight="medium" transform="uppercase" color="#fff">
                     New
                  </Text>
               </View>
            </View>
         )}
      </View>
   )
}

MessageListItem.propTypes = {
   id: PropTypes.string.isRequired,
   allowMarkClosingMessage: PropTypes.bool,
   allowMessageCreate: PropTypes.bool.isRequired,
   anon: PropTypes.bool.isRequired,
   authorAvatarUrl: PropTypes.string,
   authorBadges: PropTypes.array,
   authorId: PropTypes.string,
   authorName: PropTypes.string,
   body: PropTypes.object.isRequired,
   borderStyle: PropTypes.string,
   currentUserCanModerate: PropTypes.bool.isRequired,
   dateAdded: PropTypes.string.isRequired,
   edited: PropTypes.bool.isRequired,
   flaggedByMe: PropTypes.bool,
   isClosingMessage: PropTypes.bool,
   isLikedByMe: PropTypes.bool.isRequired,
   isMine: PropTypes.bool.isRequired,
   numLikes: PropTypes.number.isRequired,
   onCloseMessageButtonClick: PropTypes.func,
   replies: PropTypes.array.isRequired,
   showNewMessageAnchor: PropTypes.bool,
   textColor: PropTypes.string,
   textLineHeight: PropTypes.string,
   textSize: PropTypes.string
}

MessageListItem.defaultProps = {
   allowMarkClosingMessage: false,
   authorAvatarUrl: undefined,
   authorBadges: [],
   authorId: undefined,
   authorName: undefined,
   borderStyle: '1px solid #e5e5e5',
   flaggedByMe: false,
   isClosingMessage: false,
   onCloseMessageButtonClick: undefined,
   showNewMessageAnchor: false,
   textColor: '#222',
   textLineHeight: '160%',
   textSize: '2'
}

export default MessageListItem
